/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */

import { Blocks, DramaIcon, Search } from 'lucide-react';
import {
    CornerDownLeft,
    CommandIcon,
    TextCursorInput,
    ArrowUpCircle,
    List,
    CurlyBraces,
    Link2,
    MailCheck,
    Clock,
    Microscope,
} from 'lucide-react';
import { MousePointer2 } from 'lucide-react';

import { AgentStepKind, TargetingStrategy } from '@/commons/data';

export type RecordedAction = {
    kind: string;
    name: string;
    value?: string;
    selectors: {
        [key: string]: {
            selector: string;
            unique: boolean;
            code?: string;
        };
    };
    targetDescription?: string;
    hid: string;
    startTime: number;
    beforeActionTimeStamp: number;
    afterActionTimeStamp: number;
    result: 'PASS';
    bugs: [];
    error?: string;
    observations?: string;
    variablesBefore?: [string, string][];
    variablesAfter?: [string, string][];
    variables?: { [key: string]: string };
    extractedData?: { [key: string]: string };
};

/** List of actions that can be added to a story */
export const actionProps = {
    leftClick: {
        label: 'Left click',
        icon: MousePointer2,
        type: 'single',
        step: {
            kind: 'aiAtomic',
            action: 'leftClick',
        },
        paramsMap: {
            target: 'target',
            intent: 'intent',
        },
    },
    typeText: {
        label: 'Type text',
        icon: TextCursorInput,
        type: 'single',
        step: {
            kind: 'aiAtomic',
            action: 'typeText',
        },
        paramsMap: {
            target: 'target',
            value: 'value',
            intent: 'intent',
            typingMode: 'typingMode',
        },
    },
    pressKey: {
        label: 'Press key',
        icon: CommandIcon,
        type: 'single',
        step: {
            kind: 'pressKey',
        },
        paramsMap: {
            value: 'key',
        },
    },
    pressEnter: {
        label: 'Press enter',
        icon: CornerDownLeft,
        type: 'single',
        step: {
            kind: 'aiAtomic',
            action: 'pressEnter',
        },
        paramsMap: {
            target: 'target',
            intent: 'intent',
        },
    },
    selectOption: {
        label: 'Select',
        icon: List,
        type: 'single',
        step: {
            kind: 'aiAtomic',
            action: 'selectOption',
        },
        paramsMap: {
            target: 'target',
            value: 'value',
            intent: 'intent',
        },
    },
    hover: {
        label: 'Hover',
        icon: ArrowUpCircle,
        type: 'single',
        step: {
            kind: 'aiAtomic',
            action: 'hover',
        },
        paramsMap: {
            target: 'target',
            intent: 'intent',
        },
    },
    extractSelector: {
        label: 'Extract a selector',
        icon: Search,
        type: 'single',
        step: {
            kind: 'aiAtomic',
            action: 'extractSelector',
        },
        paramsMap: {
            target: 'target',
            value: 'variableName',
        },
    },
    gotoUrl: {
        label: 'Go to url',
        icon: Link2,
        type: 'single',
        step: {
            kind: 'gotoUrl',
        },
        paramsMap: {
            target: 'url',
        },
    },
    aiExtractInfo: {
        label: 'Extract info from page',
        icon: CurlyBraces,
        type: 'single',
        step: {
            kind: 'aiExtractInfo',
        },
        paramsMap: {
            target: 'description',
            value: 'variableName',
        },
    },
    emailLogin: {
        label: 'Login with magic link',
        icon: MailCheck,
        type: 'extra',
        step: {
            kind: 'emailLogin',
        },
        paramsMap: {
            target: 'address',
        },
    },
    emailSignup: {
        label: 'Verify email',
        icon: MailCheck,
        type: 'extra',
        step: {
            kind: 'emailSignup',
        },
        paramsMap: {
            target: 'signupMethod',
            intent: 'intent',
            value: 'saveCodeToVariable',
        },
    },
    readSMS: {
        label: 'Read SMS',
        icon: MailCheck,
        type: 'extra',
        step: {
            kind: 'readSMS',
        },
        paramsMap: {
            target: 'phoneNumber',
            value: 'toVariableName',
        },
    },
    execPlaywright: {
        label: 'Execute Playwright code',
        icon: DramaIcon,
        type: 'extra',
        step: {
            kind: 'execPlaywright',
        },
        paramsMap: {
            target: 'pwCode',
        },
    },
    wait: {
        label: 'Wait for a given time',
        icon: Clock,
        type: 'single',
        step: {
            kind: 'wait',
        },
        paramsMap: {
            target: 'durationMs',
        },
    },
    simpleAssertion: {
        label: 'Assertion',
        icon: Microscope,
        type: 'assertion',
        step: {
            kind: 'simpleAssertion',
        },
        paramsMap: {
            target: 'input',
            value: 'value',
            operator: 'operator',
        },
    },
    aiAssertion: {
        label: 'AI assertion',
        icon: Microscope,
        type: 'assertion',
        step: {
            kind: 'aiAssertion',
        },
        paramsMap: {
            target: 'assertion',
        },
    },
    recordedActions: {
        label: 'Show the agent',
        icon: MousePointer2,
        type: 'recorder',
        step: {
            kind: 'recordedActions',
        },
        paramsMap: {
            target: 'intent',
        },
    },
    block: {
        label: 'Block',
        icon: Blocks,
        type: 'block',
        step: {
            kind: 'block',
        },
        paramsMap: {
            target: 'target',
        },
    },
};

export type Step = {
    kind: Exclude<AgentStepKind, 'aiAgent'> | 'aiPrompt' | 'block' | 'separator' | 'empty';
    action?: keyof typeof actionProps;
    running?: boolean;
    recording?: boolean;
    actions?: RecordedAction[];
    recorded?: RecordedAction[];
    value?: string;
    key?: string;
    target?: string;
    id: string;
    executed?: boolean;
    intent?: string;
    url?: string;
    typingMode?: 'fill' | 'pressSequentially';
    signupMethod?: string;
    phoneNumber?: string;
    toVariableName?: string;
    assertion?: string;
    input?: string;
    operator?: string;
    pwCode?: string;
    address?: string;
    durationMs?: string;
    variableName?: string;
    description?: string;
    saveCodeToVariable?: string;
    mediaAssets?: {
        [key: string]: { url: string; resolvedURL?: string };
    };
    name?: string;
    slug?: string;
    blockSteps?: Step[];
    blockStoryId?: number;
    targetingStrategy?: TargetingStrategy;
    intentHash?: string | null;
    intentPrompt?: string | null;
    result?: 'PASS' | 'FAIL' | 'CRASH' | 'UNKNOWN' | 'CANCELLED';
};

export type Story = {
    id: number;
    name: string;
    slug: string;
    entryHref: string;
    steps: Step[];
    archived?: boolean;
    variables?: { [key: string]: string };
    featureFlags?: Record<string, string>;
    block?: boolean;
};
